import { useUser } from '@clerk/clerk-react';
import { useState } from 'react';
import { toastStore } from '@/shared/ui/toast';

// Maps Clerk's error shape to a single readable message. Clerk throws a
// `ClerkAPIResponseError` carrying `errors: [{ message, longMessage }]`; we
// surface the most specific text (wrong current password, breached password,
// etc.) rather than a generic failure.
const clerkErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'errors' in error) {
    const errors = (
      error as { errors?: { longMessage?: string; message?: string }[] }
    ).errors;
    const first = errors?.at(0);
    if (first?.longMessage) return first.longMessage;
    if (first?.message) return first.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Could not change password';
};

type ChangePasswordArgs = {
  currentPassword: string;
  newPassword: string;
};

// Wraps Clerk's `user.updatePassword`. Password is Clerk-owned — there is no
// backend procedure. Returns an async `changePassword` plus a pending flag for
// the submit button. Success/error feedback goes through the global toast.
export const useChangePassword = () => {
  const { user } = useUser();
  const [isPending, setIsPending] = useState(false);

  const changePassword = async ({
    currentPassword,
    newPassword,
  }: ChangePasswordArgs): Promise<boolean> => {
    if (!user) return false;
    setIsPending(true);
    try {
      await user.updatePassword({ currentPassword, newPassword });
      toastStore.getState().show({ message: 'Password changed' });
      return true;
    } catch (error) {
      toastStore.getState().show({ message: clerkErrorMessage(error) });
      return false;
    } finally {
      setIsPending(false);
    }
  };

  return { changePassword, isPending };
};
